import { Capacitor } from '@capacitor/core';
import { Health, type HealthSample } from '@capgo/capacitor-health';

import type {
  AuthStatus,
  DailyMetric,
  DailyMetricResult,
  DateRange,
  HealthDataReader,
  HealthScope,
  PermissionResult,
  ProbeResult,
} from './HealthDataReader';
import { fillMissingDays, toLocalDateKey } from './dateUtils';

const HEALTHKIT_ASKED_KEY = 'healthkit_asked';

/** Only sleep samples in these states count toward "time asleep". */
const ASLEEP_STATES = new Set<string>(['asleep', 'rem', 'deep', 'light']);

/**
 * Plugin datatype string per scope. `activeEnergy` uses `'calories'` to
 * match the working `useHealthKit.ts` path — the previous
 * `'activeEnergyBurned'` identifier is not recognised by
 * @capgo/capacitor-health and silently returned zero rows.
 */
const DATATYPE: Record<HealthScope, string> = {
  sleep: 'sleep',
  restingHR: 'restingHeartRate',
  hrv: 'heartRateVariability',
  steps: 'steps',
  activeEnergy: 'calories',
  vo2Max: 'vo2Max',
};

const aggregateByDay = (
  samples: HealthSample[],
  mode: 'sum' | 'avg',
): Map<string, number> => {
  const buckets = new Map<string, { total: number; count: number }>();
  for (const s of samples) {
    const key = toLocalDateKey(new Date(s.startDate));
    const cur = buckets.get(key) ?? { total: 0, count: 0 };
    cur.total += s.value ?? 0;
    cur.count += 1;
    buckets.set(key, cur);
  }
  const out = new Map<string, number>();
  for (const [k, v] of buckets) {
    if (v.count === 0) continue;
    out.set(k, mode === 'sum' ? v.total : v.total / v.count);
  }
  return out;
};

export class HealthKitReader implements HealthDataReader {
  readonly platform = 'healthkit' as const;

  isAvailable(): boolean {
    return Capacitor.getPlatform() === 'ios' && Capacitor.isNativePlatform();
  }

  async requestPermissions(scopes: HealthScope[]): Promise<PermissionResult> {
    if (!this.isAvailable() || scopes.length === 0) return { granted: [] };

    const readTypes: string[] = scopes.map((s) => DATATYPE[s]);

    try {
      await Health.requestAuthorization({
        read: readTypes as never,
        write: [],
      });
    } catch {
      return { granted: [] };
    }
    try {
      localStorage.setItem(HEALTHKIT_ASKED_KEY, 'true');
    } catch {
      // ignore
    }

    const now = new Date();
    const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const startIso = start.toISOString();
    const endIso = now.toISOString();

    // Data-presence probe (diagnostic only — not the grant source of truth).
    const probe = async (dataType: string): Promise<ProbeResult> => {
      try {
        if (dataType === 'steps' || dataType === 'calories') {
          const res = await Health.queryAggregated({
            dataType: dataType as never,
            startDate: startIso,
            endDate: endIso,
            bucket: 'day',
            aggregation: 'sum',
          });
          const count = (res.samples ?? []).length;
          return { result: count > 0 ? 'granted+data' : 'granted+empty', sampleCount: count };
        }
        const res = await Health.readSamples({
          dataType: dataType as never,
          startDate: startIso,
          endDate: endIso,
          limit: 500,
          ascending: false,
        });
        const count = (res.samples ?? []).length;
        return { result: count > 0 ? 'granted+data' : 'granted+empty', sampleCount: count };
      } catch (e) {
        return {
          result: 'threw',
          error: e instanceof Error ? e.message : String(e),
        };
      }
    };

    // Authorisation check — source of truth for `granted_scopes`.
    // Wrapped defensively because `checkAuthorization` isn't available in
    // every plugin version; fall back to 'unknown' if missing.
    const checkAuth = async (dataType: string): Promise<AuthStatus> => {
      try {
        const anyHealth = Health as unknown as {
          checkAuthorization?: (opts: { read: string[]; write?: string[] }) => Promise<{
            read?: Record<string, string>;
            status?: string;
            authorized?: boolean;
          }>;
          isAuthorized?: (opts: { read: string[]; write?: string[] }) => Promise<{
            authorized?: boolean;
          }>;
        };
        if (typeof anyHealth.checkAuthorization === 'function') {
          const res = await anyHealth.checkAuthorization({ read: [dataType], write: [] });
          const raw =
            res?.read?.[dataType] ??
            res?.status ??
            (res?.authorized === true ? 'authorized' : undefined);
          if (raw === 'authorized' || raw === 'sharingAuthorized') return 'authorized';
          if (raw === 'denied' || raw === 'sharingDenied') return 'denied';
          if (raw === 'notDetermined') return 'notDetermined';
          return 'unknown';
        }
        if (typeof anyHealth.isAuthorized === 'function') {
          const res = await anyHealth.isAuthorized({ read: [dataType], write: [] });
          return res?.authorized ? 'authorized' : 'unknown';
        }
        return 'unknown';
      } catch {
        return 'unknown';
      }
    };

    const granted: HealthScope[] = [];
    const probes: Partial<Record<HealthScope, ProbeResult>> = {};
    const authStatus: Partial<Record<HealthScope, AuthStatus>> = {};
    for (const scope of scopes) {
      const dataType = DATATYPE[scope];
      const [pr, auth] = await Promise.all([probe(dataType), checkAuth(dataType)]);
      probes[scope] = pr;
      authStatus[scope] = auth;
      // Combined-logic grant: iOS `checkAuthorization` deliberately returns
      // `notDetermined`/`unknown` for READ scopes even after the user grants
      // them (Apple hides read auth state for privacy). So we fall back to
      // the data-presence probe: a scope is "granted" if EITHER
      // checkAuthorization confirms it OR samples actually came back.
      // Only treated as denied/unknown when BOTH signals are negative.
      if (auth === 'authorized' || pr.result === 'granted+data') {
        granted.push(scope);
      }
    }
    return { granted, authStatus, probes };
  }


  async getSleep(range: DateRange): Promise<DailyMetricResult> {
    if (!this.isAvailable()) return { metrics: [], sampleCount: 0 };
    try {
      const res = await Health.readSamples({
        dataType: 'sleep',
        startDate: range.start.toISOString(),
        endDate: range.end.toISOString(),
        limit: 5000,
        ascending: true,
      });
      const samples = res.samples ?? [];
      const byDay = new Map<string, number>();
      const inBedByDay = new Map<string, number>();
      for (const s of samples) {
        const start = new Date(s.startDate).getTime();
        const end = new Date(s.endDate).getTime();
        if (Number.isNaN(start) || Number.isNaN(end) || end <= start) continue;
        const key = toLocalDateKey(new Date(end));
        const minutes = (end - start) / 60_000;
        const state = s.sleepState;
        if (state && !ASLEEP_STATES.has(state)) {
          // Fallback bucket: iOS Bedtime / third-party apps that only write inBed.
          if (state === 'inBed') inBedByDay.set(key, (inBedByDay.get(key) ?? 0) + minutes);
          continue;
        }
        byDay.set(key, (byDay.get(key) ?? 0) + minutes);
      }
      // Prefer real asleep states; only use inBed where the day has none.
      for (const [k, v] of inBedByDay) {
        if (!byDay.has(k)) byDay.set(k, v);
      }
      const rounded = new Map<string, number>();
      for (const [k, v] of byDay) rounded.set(k, Math.round(v));
      return { metrics: fillMissingDays(range, rounded), sampleCount: samples.length };
    } catch {
      return { metrics: [], sampleCount: 0 };
    }
  }

  async getRestingHR(range: DateRange): Promise<DailyMetricResult> {
    if (!this.isAvailable()) return { metrics: [], sampleCount: 0 };
    try {
      const res = await Health.readSamples({
        dataType: 'restingHeartRate',
        startDate: range.start.toISOString(),
        endDate: range.end.toISOString(),
        limit: 1000,
        ascending: true,
      });
      const samples = res.samples ?? [];
      const byDay = aggregateByDay(samples, 'avg');
      const rounded = new Map<string, number>();
      for (const [k, v] of byDay) rounded.set(k, Math.round(v));
      return { metrics: fillMissingDays(range, rounded), sampleCount: samples.length };
    } catch {
      return { metrics: [], sampleCount: 0 };
    }
  }

  async getHRV(range: DateRange): Promise<DailyMetricResult> {
    if (!this.isAvailable()) return { metrics: [], sampleCount: 0 };
    try {
      const res = await Health.readSamples({
        dataType: 'heartRateVariability',
        startDate: range.start.toISOString(),
        endDate: range.end.toISOString(),
        limit: 5000,
        ascending: true,
      });
      const samples = res.samples ?? [];
      const byDay = aggregateByDay(samples, 'avg');
      const rounded = new Map<string, number>();
      for (const [k, v] of byDay) rounded.set(k, Math.round(v * 100) / 100);
      return { metrics: fillMissingDays(range, rounded), sampleCount: samples.length };
    } catch {
      return { metrics: [], sampleCount: 0 };
    }
  }

  async getSteps(range: DateRange): Promise<DailyMetricResult> {
    return this.getAggregatedQuantity('steps', range, 'steps');
  }

  async getActiveEnergy(range: DateRange): Promise<DailyMetricResult> {
    return this.getAggregatedQuantity('calories', range, 'activeEnergy');
  }

  /**
   * VO₂ max — sparse quantity samples (Apple Watch estimates every few
   * days). We query the plugin over `range`; if empty we widen to the last
   * 365 days to capture the most recent reading. Returned as a
   * single-entry `DailyMetricResult` on the sample date, unit ml/kg/min.
   */
  async getVO2Max(range: DateRange): Promise<DailyMetricResult> {
    if (!this.isAvailable()) return { metrics: [], sampleCount: 0 };
    const read = async (start: Date, end: Date) => {
      return Health.readSamples({
        dataType: 'vo2Max' as never,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        limit: 500,
        ascending: false,
      });
    };
    try {
      let res = await read(range.start, range.end);
      let samples = res.samples ?? [];
      if (samples.length === 0) {
        const wideEnd = new Date();
        const wideStart = new Date(wideEnd.getTime() - 365 * 24 * 60 * 60 * 1000);
        res = await read(wideStart, wideEnd);
        samples = res.samples ?? [];
      }
      if (samples.length === 0) return { metrics: [], sampleCount: 0 };
      // Most recent first (ascending:false) — take the top sample.
      const latest = samples[0];
      const value = latest.value ?? null;
      if (value == null) return { metrics: [], sampleCount: samples.length };
      return {
        metrics: [{ date: toLocalDateKey(new Date(latest.endDate ?? latest.startDate)), value }],
        sampleCount: samples.length,
      };
    } catch {
      return { metrics: [], sampleCount: 0 };
    }
  }

  private async getAggregatedQuantity(
    dataType: 'steps' | 'calories',
    range: DateRange,
    logScope: HealthScope,
  ): Promise<DailyMetricResult> {
    if (!this.isAvailable()) return { metrics: [], sampleCount: 0 };
    try {
      const res = await Health.queryAggregated({
        dataType: dataType as never,
        startDate: range.start.toISOString(),
        endDate: range.end.toISOString(),
        bucket: 'day',
        aggregation: 'sum',
      });
      const samples = res.samples ?? [];
      const byDay = new Map<string, number>();
      for (const sample of samples) {
        const key = toLocalDateKey(new Date(sample.startDate));
        byDay.set(key, (byDay.get(key) ?? 0) + (sample.value || 0));
      }
      const rounded = new Map<string, number>();
      for (const [k, v] of byDay) rounded.set(k, Math.round(v));
      return { metrics: fillMissingDays(range, rounded), sampleCount: samples.length };
    } catch {
      return { metrics: [], sampleCount: 0 };
    }
  }
}
